import { useState, useCallback, useEffect } from 'react';
import { Assistant, AssistantConversation, ToolCallResult } from '../types/assistant';
import { Conversation, Message, AIProvider } from '../types/ai';
import { User, Tenant } from '../types/user';
import { NewChatInterface } from './NewChatInterface';
import { AssistantSharingModal } from './AssistantSharingModal';
import { assistantService } from '../services/assistant-service';
import { aiService } from '../services/ai-service';
import { plansService } from '../services/plans-service';
import { userManagementService } from '../services/user-management-service';
import { toolExecutorService } from '../services/tool-executor-service';
import { aiProviders } from '../data/ai-providers';
import { useToast } from '../hooks/use-toast';
import { processSpreadsheet, isSpreadsheet, processPDF, isPDF } from '../utils/file-processor';
import { vectorKnowledgeService } from '../services/vector-knowledge-service';
import { formatErrorMessage } from '../utils/error-messages';

interface AssistantChatWrapperProps {
  assistant: Assistant;
  user: User;
  tenant: Tenant;
  conversation: AssistantConversation | null;
  onConversationChange: (conversation: AssistantConversation) => void;
  onUsageUpdate?: () => void;
  onShowUpsell?: () => void;
  onEditAssistant?: () => void;
  onBackClick?: () => void;
  onNewConversation?: () => void;
}

export function AssistantChatWrapper({
  assistant,
  user,
  tenant,
  conversation,
  onConversationChange,
  onUsageUpdate,
  onShowUpsell,
  onEditAssistant,
  onBackClick,
  onNewConversation
}: AssistantChatWrapperProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(assistant.modelId);
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [tenantUsers, setTenantUsers] = useState<User[]>([]);
  const [currentAssistant, setCurrentAssistant] = useState<Assistant>(assistant);
  const [runningToolCalls, setRunningToolCalls] = useState<ToolCallResult[]>([]);
  const toast = useToast();

  // Sincronizar modelo com agente
  useEffect(() => {
    console.log('🔧 [AssistantChatWrapper] Agente recebido:', {
      id: assistant.id,
      name: assistant.name,
      hasTools: !!assistant.tools,
      toolsCount: assistant.tools?.length || 0,
      tools: assistant.tools
    });
    setSelectedModel(assistant.modelId);
    setCurrentAssistant(assistant);
  }, [assistant]);

  // Carregar usuários do tenant se for Business
  useEffect(() => {
    if (tenant.accountType === 'business' && assistant.createdBy === user.id) {
      loadTenantUsers();
    }
  }, [tenant.id, tenant.accountType, assistant.createdBy, user.id]);

  const loadTenantUsers = async () => {
    try {
      const users = await userManagementService.getTenantUsers(tenant.id);
      setTenantUsers(users);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  // Criar um provider virtual a partir do agente
  const provider: AIProvider = aiProviders.find(p => p.id === assistant.aiProvider) || {
    id: assistant.aiProvider,
    name: assistant.name,
    icon: assistant.avatar || '🤖',
    logo: assistant.avatar || '🤖',
    color: 'bg-blue-600',
    description: assistant.description,
    models: [
      {
        id: assistant.modelId,
        name: assistant.name,
        description: assistant.description
      }
    ]
  };

  // Converter AssistantConversation para Conversation (preservar toolCalls para exibir execução das tools)
  const convertedConversation: Conversation | null = conversation ? {
    id: conversation.id,
    title: conversation.title,
    aiId: assistant.aiProvider,
    modelId: assistant.modelId,
    userId: conversation.userId,
    tenantId: conversation.tenantId,
    messageCount: conversation.messages?.length || 0,
    messages: conversation.messages.map(msg => ({
      ...msg,
      id: msg.id,
      content: msg.content,
      role: msg.role as 'user' | 'assistant' | 'system',
      timestamp: msg.timestamp,
      modelId: assistant.modelId,
      modelName: assistant.name,
      attachments: msg.attachments,
      messageType: msg.messageType,
      toolId: msg.toolId,
      toolName: msg.toolName,
      toolOperation: msg.toolOperation,
      toolParameters: msg.toolParameters,
      toolResult: msg.toolResult,
      toolStatus: msg.toolStatus,
      toolCalls: msg.toolCalls ?? (msg as any).toolCalls,
      generatedFiles: msg.generatedFiles ?? (msg as any).generatedFiles
    })),
    createdAt: conversation.createdAt,
    updatedAt: conversation.updatedAt
  } : null;

  const handleSendMessage = useCallback(async (content: string, _aiId?: string, _modelId?: string, files?: File[]) => {
    console.log('🔍 [AssistantChatWrapper] handleSendMessage chamado:', { 
      hasFiles: !!files, 
      filesCount: files?.length || 0, 
      fileNames: files?.map(f => f.name) 
    });
    
    // ✅ VERIFICAR LIMITE DE MENSAGENS ANTES DE ENVIAR
    const canSend = await plansService.canSendMessage(tenant.id, user.id);
    if (!canSend) {
      console.log('❌ Limite de mensagens atingido! Abrindo modal de upsell...');
      if (onShowUpsell) {
        onShowUpsell();
      }
      toast.error('Você atingiu o limite de mensagens diárias do plano gratuito');
      return;
    }

    const messageContent = content.trim();
    setIsLoading(true);
    let currentConversation: AssistantConversation | null = conversation;

    try {
      // Criar ou obter conversa
      if (!currentConversation) {
        const title = messageContent || (files && files.length > 0 ? `Arquivo: ${files[0].name}` : 'Nova conversa');
        currentConversation = await assistantService.createAssistantConversation(
          assistant.id,
          user.id,
          tenant.id,
          title.slice(0, 50) + (title.length > 50 ? '...' : '')
        );
        onConversationChange(currentConversation);
      }

      // Processar anexos
      const attachments = files ? await Promise.all(
        files.map(async (file) => {
          let url: string | undefined;
          if (file.type.startsWith('image/')) {
            url = URL.createObjectURL(file);
          }
          return {
            name: file.name,
            size: file.size,
            type: file.type,
            file: file,
            url
          };
        })
      ) : undefined;

      // ✨ Processar arquivos (PDF/Excel/CSV) e extrair conteúdo
      let enrichedContent = messageContent;
      
      if (files && files.length > 0) {
        console.log('📎 [AssistantChatWrapper] Processando arquivos anexados...', files.map(f => ({
          name: f.name,
          type: f.type,
          size: f.size,
          isPDF: isPDF(f),
          isSpreadsheet: isSpreadsheet(f)
        })));
        
        // Processar planilhas (Excel/CSV)
        const spreadsheetFiles = files.filter(isSpreadsheet);
        console.log('📊 Filtrando planilhas:', spreadsheetFiles.length);
        if (spreadsheetFiles.length > 0) {
          console.log(`📊 Encontrados ${spreadsheetFiles.length} arquivo(s) Excel/CSV`);
          
          for (const file of spreadsheetFiles) {
            try {
              const spreadsheetData = await processSpreadsheet(file);
              enrichedContent += `\n\n${spreadsheetData}`;
              console.log(`✅ Planilha ${file.name} processada com sucesso`);
            } catch (error) {
              console.error(`❌ Erro ao processar ${file.name}:`, error);
              toast.error(`Erro ao processar ${file.name}`);
            }
          }
        }
        
        // Processar PDFs
        const pdfFiles = files.filter(isPDF);
        console.log('📄 Filtrando PDFs:', pdfFiles.length);
        if (pdfFiles.length > 0) {
          console.log(`📄 Encontrados ${pdfFiles.length} arquivo(s) PDF`);
          
          for (const file of pdfFiles) {
            try {
              console.log(`📄 Processando PDF: ${file.name}...`);
              toast.info(`Processando PDF: ${file.name}...`);
              const pdfData = await processPDF(file);
              enrichedContent += `\n\n${pdfData}`;
              console.log(`✅ PDF ${file.name} processado com sucesso`);
              toast.success(`PDF ${file.name} processado!`);
            } catch (error) {
              console.error(`❌ Erro ao processar PDF ${file.name}:`, error);
              toast.error(`Erro ao processar PDF ${file.name}`);
            }
          }
        }
      } else {
        console.log('📎 [AssistantChatWrapper] Nenhum arquivo para processar');
      }

      // ✅ SEPARAR: texto para salvar no banco vs. texto para enviar ao GPT
      // No banco e no frontend: apenas o texto original do usuário
      const displayContent = messageContent || (files && files.length > 0 ? '[Arquivo enviado]' : '');
      
      // Para o GPT: texto original + conteúdo extraído dos arquivos
      const contentForAI = enrichedContent || displayContent;

      // Adicionar mensagem do usuário (SEM conteúdo extraído - apenas texto original)
      const userMessage = await assistantService.addMessageToAssistantConversation(
        currentConversation.id,
        assistant.id,
        displayContent, // ✅ Salvar apenas texto original
        'user',
        undefined,
        undefined,
        undefined,
        attachments
      );

      // Atualizar conversa localmente COM attachments incluindo File objects
      const userMessageWithFiles = attachments ? {
        ...userMessage,
        attachments, // ✅ Incluir attachments COM File objects para a IA processar
        enrichedContent: contentForAI // ✅ Armazenar conteúdo enriquecido separadamente para enviar ao GPT
      } : {
        ...userMessage,
        enrichedContent: contentForAI
      };

      let updatedConversation = {
        ...currentConversation,
        messages: [...(currentConversation.messages || []), userMessageWithFiles]
      };
      onConversationChange(updatedConversation);

      // ✅ Garantir thread completo: recarregar conversa do servidor para ter todo o histórico
      let fullConversation = await assistantService.getAssistantConversationWithMessages(currentConversation.id);
      if (fullConversation?.messages?.length) {
        const fromDb = fullConversation.messages;
        const lastFromDb = fromDb[fromDb.length - 1];
        const threadMessages =
          lastFromDb?.id === userMessageWithFiles.id
            ? [...fromDb.slice(0, -1), userMessageWithFiles]
            : [...fromDb, userMessageWithFiles];
        updatedConversation = { ...updatedConversation, messages: threadMessages };
      }

      // 🔍 BUSCAR CONHECIMENTO RELEVANTE NA BASE DE CONHECIMENTO
      console.log('🔍 [AssistantChatWrapper] Buscando na base de conhecimento...', {
        assistantId: assistant.id,
        query: messageContent,
        queryLength: messageContent.length
      });
      
      const knowledgeResults = await vectorKnowledgeService.searchDocuments(
        assistant.id,
        messageContent,
        3 // Top 3 resultados mais relevantes
      );

      console.log('📊 [AssistantChatWrapper] Resultados da busca:', {
        total: knowledgeResults.length,
        documentos: knowledgeResults.map(r => ({
          title: r.title,
          relevance: r.relevance,
          contentLength: r.content.length
        }))
      });

      // Preparar contexto da base de conhecimento
      let knowledgeContext = '';
      if (knowledgeResults.length > 0) {
        console.log('✅ [AssistantChatWrapper] Injetando contexto da base de conhecimento');
        
        knowledgeContext = '\n\n' + '='.repeat(80) + '\n';
        knowledgeContext += '📚 BASE DE CONHECIMENTO DO AGENTE\n';
        knowledgeContext += '='.repeat(80) + '\n\n';
        knowledgeContext += '⚠️ IMPORTANTE: Use as informações abaixo da base de conhecimento para responder.\n';
        knowledgeContext += 'Se a pergunta do usuário estiver relacionada a algum destes documentos, RESPONDA COM BASE NELES.\n\n';
        
        knowledgeResults.forEach((result, index) => {
          knowledgeContext += `📄 DOCUMENTO ${index + 1}: ${result.title}\n`;
          knowledgeContext += `   Relevância: ${(result.relevance * 100).toFixed(0)}%\n`;
          knowledgeContext += `\n${result.content}\n\n`;
          knowledgeContext += '-'.repeat(80) + '\n\n';
        });
        
        knowledgeContext += '='.repeat(80) + '\n';
        knowledgeContext += '📚 FIM DA BASE DE CONHECIMENTO\n';
        knowledgeContext += '='.repeat(80) + '\n';
      } else {
        console.log('⚠️ [AssistantChatWrapper] Nenhum documento encontrado na base de conhecimento');
      }

      // Preparar mensagens para IA com instruções do agente
      const allMessages = updatedConversation.messages;
      
      // 🔧 Gerar prompt de ferramentas se houver tools configuradas
      const toolsPrompt = currentAssistant.tools && currentAssistant.tools.length > 0 
        ? toolExecutorService.generateToolsPrompt(currentAssistant.tools) 
        : '';
      
      console.log('🔧 [AssistantChatWrapper] Tools config:', {
        hasTools: currentAssistant.tools && currentAssistant.tools.length > 0,
        toolsCount: currentAssistant.tools?.length || 0,
        tools: currentAssistant.tools,
        promptLength: toolsPrompt.length
      });

      // Se tem ferramentas, colocar as instruções NO INÍCIO e de forma muito enfática
      const systemMessage = currentAssistant.tools && currentAssistant.tools.length > 0 
        ? `${toolsPrompt}

Você é ${assistant.name}.
${assistant.description ? `Descrição: ${assistant.description}` : ''}

INSTRUÇÕES DO AGENTE:
${assistant.instructions}
${knowledgeContext}`
        : `Você é ${assistant.name}.
${assistant.description ? `Descrição: ${assistant.description}` : ''}

INSTRUÇÕES IMPORTANTES (SIGA RIGOROSAMENTE):
${assistant.instructions}

Lembre-se: Você DEVE seguir as instruções acima em todas as suas respostas.${knowledgeContext}`;
      
      const aiMessages = [
        { 
          id: 'system', 
          content: systemMessage, 
          role: 'system' as const, 
          timestamp: new Date(), 
          modelId: assistant.modelId, 
          modelName: assistant.name 
        },
        ...allMessages.map(msg => {
          const content = (msg as any).enrichedContent || msg.content || '';
          const isToolMessage = msg.role === 'system' && ((msg as any).messageType === 'tool' || (msg as any).toolName);
          // Mensagens de ferramenta (system) viram "user" com prefixo para a API ver o contexto (muitas APIs usam só a primeira system)
          const role = isToolMessage ? 'user' as const : (msg.role as 'user' | 'assistant');
          const displayContent = isToolMessage
            ? `[Resultado da ferramenta ${(msg as any).toolName || 'ferramenta'}]:\n${content}`
            : content;
          return {
            id: msg.id,
            content: displayContent,
            role,
            timestamp: msg.timestamp,
            modelId: assistant.modelId,
            modelName: assistant.name,
            attachments: msg.attachments?.filter(att => att.file instanceof File)
          };
        })
      ];
      
      // Extrair IDs das tools ativas para passar ao aiService
      const enabledToolIds = currentAssistant.tools
        ?.filter(t => t.isActive)
        .map(t => t.toolId) || [];
      
      // Chamar IA
      let aiResult = await aiService.sendMessage(
        assistant.aiProvider,
        aiMessages as any,
        assistant.modelId,
        undefined,
        {
          tenantId: tenant.id,
          userId: user.id,
          enabledTools: enabledToolIds
        },
        files
      );

      // 🔧 PROCESSAR CHAMADAS DE FERRAMENTAS
      let finalContent = aiResult.content;
      let toolCallResults: ToolCallResult[] = [];
      
      console.log('🔧 [AssistantChatWrapper] Verificando tools na resposta:', {
        hasTools: currentAssistant.tools && currentAssistant.tools.length > 0,
        toolsCount: currentAssistant.tools?.length || 0,
        responsePreview: aiResult.content.substring(0, 300),
        // ✨ NOVO: Verificar toolCalls retornadas pelo ai-service (OpenAI function calling)
        hasAiResultToolCalls: !!aiResult.toolCalls,
        aiResultToolCallsCount: aiResult.toolCalls?.length || 0
      });
      
      // ✨ USAR toolCalls JÁ RETORNADAS pelo ai-service (via OpenAI function calling)
      if (aiResult.toolCalls && aiResult.toolCalls.length > 0) {
        console.log('✅ [AssistantChatWrapper] Usando toolCalls do ai-service:', aiResult.toolCalls.length);
        toolCallResults = aiResult.toolCalls as ToolCallResult[];
        // Persistir cada tool no histórico para não sumir ao recarregar
        for (const result of toolCallResults) {
          const toolMsg = await assistantService.addMessageToAssistantConversation(
            currentConversation.id,
            assistant.id,
            result.status === 'completed' ? `Executado: ${result.operationName}` : `Falhou: ${result.error}`,
            'system',
            undefined,
            undefined,
            undefined,
            undefined,
            {
              messageType: 'tool',
              toolId: result.toolId,
              toolName: result.operationName,
              toolOperation: result.operationId,
              toolParameters: result.parameters,
              toolResult: result.result,
              toolStatus: result.status
            }
          );
          updatedConversation = {
            ...updatedConversation,
            messages: [...updatedConversation.messages, toolMsg]
          };
        }
        onConversationChange(updatedConversation);
      } else {
        // 🔄 FALLBACK: LOOP para processar tool calls extraídas do conteúdo (sistema antigo)
        console.log('🔄 [AssistantChatWrapper] Fallback: tentando extrair tool calls do conteúdo...');
      }
      
      // 🔄 LOOP para processar múltiplas tool calls em sequência (apenas se não tiver toolCalls do ai-service)
      let currentContent = aiResult.content;
      let currentMessages = [...aiMessages];
      let iterationCount = 0;
      const MAX_ITERATIONS = 5; // Limite de segurança para evitar loops infinitos
      
      while (toolCallResults.length === 0 && currentAssistant.tools && currentAssistant.tools.length > 0 && iterationCount < MAX_ITERATIONS) {
        const toolCalls = toolExecutorService.extractToolCalls(currentContent);
        console.log(`🔧 [Iteração ${iterationCount + 1}] Tool calls extraídas:`, toolCalls.length);
        
        if (toolCalls.length === 0) {
          // Não há mais tool calls, sair do loop
          break;
        }
        
        iterationCount++;
        console.log(`🔧 Ferramentas detectadas (iteração ${iterationCount}):`, toolCalls.length);
        
        // Criar estado inicial de todas as tools como "running"
        const initialToolStates: ToolCallResult[] = toolCalls.map(call => ({
          toolId: call.tool,
          operationId: call.operation,
          operationName: call.operation,
          parameters: call.parameters,
          status: 'running' as const,
          startedAt: new Date()
        }));
        
        // Mostrar feedback visual imediato
        setRunningToolCalls(prev => [...prev, ...initialToolStates]);
        
        // Executar TODAS as tools em paralelo
        const toolPromises = toolCalls.map(async (call, index) => {
          console.log(`🔧 Executando: ${call.tool}.${call.operation}`);
          
          try {
            const result = await toolExecutorService.executeOperation(
              call.tool,
              call.operation,
              call.parameters,
              {
                userId: user.id,
                assistantTools: currentAssistant.tools
              }
            );
            
            // Atualizar estado individual da tool
            setRunningToolCalls(prev => {
              const newPrev = [...prev];
              const targetIndex = newPrev.findIndex(tc => 
                tc.toolId === call.tool && 
                tc.operationId === call.operation && 
                tc.status === 'running'
              );
              if (targetIndex >= 0) {
                newPrev[targetIndex] = result;
              }
              return newPrev;
            });
            
            return { call, result };
          } catch (error) {
            const errorResult: ToolCallResult = {
              toolId: call.tool,
              operationId: call.operation,
              operationName: call.operation,
              parameters: call.parameters,
              status: 'failed' as const,
              error: error instanceof Error ? error.message : 'Erro desconhecido',
              startedAt: new Date(),
              completedAt: new Date()
            };
            
            setRunningToolCalls(prev => {
              const newPrev = [...prev];
              const targetIndex = newPrev.findIndex(tc => 
                tc.toolId === call.tool && 
                tc.operationId === call.operation && 
                tc.status === 'running'
              );
              if (targetIndex >= 0) {
                newPrev[targetIndex] = errorResult;
              }
              return newPrev;
            });
            
            return { call, result: errorResult };
          }
        });
        
        // Aguardar todas as tools terminarem
        const results = await Promise.all(toolPromises);
        
        // Coletar resultados e salvar cada tool como mensagem
        const toolMessages: AssistantMessage[] = [];
        for (const { call, result } of results) {
          toolCallResults.push(result);
          
          // Obter nome amigável da tool
          const toolInfo = currentAssistant.tools?.find(t => t.toolId === call.tool);
          const toolName = toolInfo ? call.tool.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : call.tool;
          
          // Salvar tool call como mensagem do tipo 'tool'
          const toolMsg = await assistantService.addMessageToAssistantConversation(
            currentConversation.id,
            assistant.id,
            result.status === 'completed' 
              ? `Executado: ${result.operationName}` 
              : `Falhou: ${result.error}`,
            'system',
            undefined,
            undefined,
            undefined,
            undefined,
            {
              messageType: 'tool',
              toolId: call.tool,
              toolName: toolName,
              toolOperation: call.operation,
              toolParameters: call.parameters,
              toolResult: result.result,
              toolStatus: result.status
            }
          );
          toolMessages.push(toolMsg);
        }
        
        // Atualizar conversa com as mensagens de tool
        updatedConversation = {
          ...updatedConversation,
          messages: [...updatedConversation.messages, ...toolMessages]
        };
        onConversationChange(updatedConversation);
        
        // Se houver tool calls bem sucedidas, enviar resultado de volta para IA
        if (results.some(r => r.result.status === 'completed')) {
          const toolResultsContext = results.map(({ result: r }) => {
            if (r.status === 'completed') {
              return `✅ ${r.operationName}: ${JSON.stringify(r.result)}`;
            }
            return `❌ ${r.operationName} falhou: ${r.error}`;
          }).join('\n\n');
          
          currentMessages = [
            ...currentMessages,
            {
              id: `tool-result-${iterationCount}`,
              content: currentContent,
              role: 'assistant' as const,
              timestamp: new Date(),
              modelId: assistant.modelId,
              modelName: assistant.name
            },
            {
              id: `tool-feedback-${iterationCount}`,
              content: `[RESULTADOS DAS FERRAMENTAS - ITERAÇÃO ${iterationCount}]\n${toolResultsContext}\n\n[INSTRUÇÃO] Continue processando. Se precisar executar mais ferramentas, use o formato \`\`\`tool. Se todas as ações necessárias foram concluídas, forneça uma resposta final natural ao usuário SEM blocos de código tool.`,
              role: 'user' as const,
              timestamp: new Date(),
              modelId: assistant.modelId,
              modelName: assistant.name
            }
          ];
          
          const followUpResult = await aiService.sendMessage(
            assistant.aiProvider,
            currentMessages as any,
            assistant.modelId,
            undefined,
            { tenantId: tenant.id, userId: user.id, enabledTools: enabledToolIds }
          );
          
          currentContent = followUpResult.content;
        } else {
          // Se todas falharam, sair do loop
          break;
        }
      }
      
      // Limpar estado de tools em execução
      setRunningToolCalls([]);
      
      // Remover qualquer bloco ```tool restante que não foi processado
      finalContent = currentContent.replace(/```tool[\s\S]*?```/g, '').trim();

      // Adicionar resposta final da IA
      const assistantMsg = await assistantService.addMessageToAssistantConversation(
        currentConversation.id,
        assistant.id,
        finalContent,
        'assistant',
        aiResult.tokens,
        assistant.modelId,
        assistant.aiProvider,
        undefined,
        {
          messageType: toolCallResults.length > 0 ? 'final_answer' : 'message'
        }
      );

      // Não anexar toolCalls na mensagem final: as tools já foram persistidas como mensagens
      // individuais no histórico. Exibir toolCalls aqui duplicaria (uma vez como mensagem de
      // tool, outra no bloco do assistente). No reload só vêm as mensagens do DB, por isso
      // lá aparecia correto.
      const finalConversation = {
        ...updatedConversation,
        messages: [...updatedConversation.messages, assistantMsg]
      };
      onConversationChange(finalConversation);

      // Incrementar contador de uso
      try {
        await plansService.incrementUsage(tenant.id, user.id, 'all');
        if (onUsageUpdate) {
          onUsageUpdate();
        }
      } catch (error) {
        console.error('Erro ao incrementar contador:', error);
      }

    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      
      const errorMessage = formatErrorMessage(error);
      toast.error(errorMessage);
      
      if (currentConversation?.id) {
        try {
          const errorMsg = await assistantService.addMessageToAssistantConversation(
            currentConversation.id,
            assistant.id,
            errorMessage,
            'assistant'
          );
          onConversationChange({
            ...currentConversation,
            messages: [...(currentConversation.messages || []), errorMsg]
          });
        } catch (e) {
          console.error('Erro ao salvar mensagem de erro na conversa:', e);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [assistant, user, tenant, conversation, onConversationChange, onUsageUpdate, onShowUpsell, toast]);

  const handleModelChange = useCallback((modelId: string) => {
    setSelectedModel(modelId);
  }, []);

  const handleProviderChange = useCallback((providerId: string) => {
    // Não permite trocar provider em chat de agente
    console.log('Provider change não permitido em chat de agente');
  }, []);

  const handleShareClick = useCallback(() => {
    setShowSharingModal(true);
  }, []);

  const handleSaveSharing = useCallback(async (visibility: 'private' | 'team' | 'shared', sharedWithUsers: string[]) => {
    try {
      await assistantService.updateAssistantSharing(
        currentAssistant.id,
        visibility,
        sharedWithUsers
      );

      // Atualizar localmente
      setCurrentAssistant(prev => ({
        ...prev,
        visibility,
        sharedWithUsers
      }));

      setShowSharingModal(false);
      toast.success('Configurações de compartilhamento salvas!');
    } catch (error) {
      console.error('Erro ao salvar compartilhamento:', error);
      toast.error('Erro ao salvar configurações');
      throw error;
    }
  }, [currentAssistant.id, toast]);

  return (
    <>
      <NewChatInterface
        provider={provider}
        availableProviders={[provider]} // Apenas o provider do agente
        conversation={convertedConversation}
        selectedModel={selectedModel}
        onModelChange={handleModelChange}
        onProviderChange={handleProviderChange}
        onSendMessage={handleSendMessage}
        isLoading={isLoading}
        assistant={currentAssistant}
        onEditAssistant={onEditAssistant}
        onShareAssistant={handleShareClick}
        onBackClick={onBackClick}
        onNewConversation={onNewConversation}
        userId={user.id}
        runningToolCalls={runningToolCalls}
      />

      {/* Modal de compartilhamento */}
      {showSharingModal && (
        <AssistantSharingModal
          assistant={currentAssistant}
          currentUser={user}
          tenantUsers={tenantUsers}
          isBusinessAccount={tenant.accountType === 'business'}
          onSave={handleSaveSharing}
          onClose={() => setShowSharingModal(false)}
        />
      )}
    </>
  );
}

